/**
 * Trace sampling that cannot lose an error.
 *
 * Traces are expensive and are sampled; errors are not and must not be. A
 * plain `TraceIdRatioBasedSampler(0)` — the cheapest way to turn tracing off
 * in an incident — would otherwise take the exception spans with it, which is
 * exactly backwards: turning tracing down is when you most need to hear about
 * failures.
 *
 * `ErrorPreservingSampler` therefore makes its own decision for the span
 * `captureException` creates (see `ERROR_SPAN_NAME`) and delegates every other
 * span to a parent-respecting ratio sampler. Error log records are written
 * whichever decision the trace took; logs are never sampled here.
 *
 * @module lib/observability/sampling
 */

import type { Attributes, Context, Link, SpanKind } from '@opentelemetry/api'
import {
  ParentBasedSampler,
  SamplingDecision,
  TraceIdRatioBasedSampler,
} from '@opentelemetry/sdk-trace-web'
import type { Sampler, SamplingResult } from '@opentelemetry/sdk-trace-web'

import { ERROR_SPAN_NAME } from './otlp'

/** Tracing on for every trace unless a deploy says otherwise. */
export const DEFAULT_TRACE_SAMPLE_RATIO = 0

/**
 * Parse a deploy-supplied ratio. Empty or malformed input keeps the default
 * rather than silently going dark, and `0` is honoured because "sample no
 * traces, keep every error" is the deliberate production setting.
 */
export function parseTraceSampleRatio(raw: string | undefined | null): number {
  if (raw === undefined || raw === null) return DEFAULT_TRACE_SAMPLE_RATIO
  const trimmed = raw.trim()
  if (trimmed === '') return DEFAULT_TRACE_SAMPLE_RATIO
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) return DEFAULT_TRACE_SAMPLE_RATIO
  if (parsed <= 0) return 0
  if (parsed >= 1) return 1
  return parsed
}

/** Ratio sampler for everything that is not an error. Parent-based: a sampled
 * browser trace keeps its server continuation sampled instead of re-rolling. */
export class ErrorPreservingSampler implements Sampler {
  private readonly delegate: Sampler
  private readonly ratio: number

  constructor(ratio: number) {
    this.ratio = parseTraceSampleRatio(`${ratio}`)
    this.delegate = new ParentBasedSampler({ root: new TraceIdRatioBasedSampler(this.ratio) })
  }

  shouldSample(
    context: Context,
    traceId: string,
    spanName: string,
    spanKind: SpanKind,
    attributes: Attributes,
    links: Link[],
  ): SamplingResult {
    if (spanName === ERROR_SPAN_NAME) {
      return { decision: SamplingDecision.RECORD_AND_SAMPLED }
    }
    try {
      return this.delegate.shouldSample(context, traceId, spanName, spanKind, attributes, links)
    } catch {
      // A sampler must never break the caller. Fall back to the configured
      // ratio's own answer rather than "always record".
      return {
        decision:
          this.ratio > 0 ? SamplingDecision.RECORD_AND_SAMPLED : SamplingDecision.NOT_RECORD,
      }
    }
  }

  toString(): string {
    return `ErrorPreservingSampler{ratio=${this.ratio}}`
  }
}

/** Convenience factory for the providers. */
export function createSampler(ratio: number): Sampler {
  return new ErrorPreservingSampler(ratio)
}
